import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import PublicHeader from './PublicHeader';
import PublicFooter from './PublicFooter';
import ScrollToTop from './ScrollToTop';
import SEOHead from './SEOHead';

const TermsPage = () => {
  const [currency, setCurrency] = useState('USD');

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Terms & Conditions - Global Trade Car"
        description="Read our terms and conditions for vehicle purchase and export services."
      />
      <PublicHeader currency={currency} onCurrencyChange={setCurrency} />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-6">Terms & Conditions</h1>
        <p className="text-gray-600 mb-8">Last updated: December 15, 2024</p>

        <Card className="mb-6">
          <CardContent className="p-8 prose prose-red max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-600 mb-4">
              Welcome to Global Trade Car. These terms and conditions outline the rules and regulations 
              for the use of our website and purchase of vehicles through our services.
            </p>
            <p className="text-gray-600 mb-4">
              By accessing this website and using our services, we assume you accept these terms and 
              conditions. Do not continue to use Global Trade Car if you do not agree to all of the 
              terms and conditions stated on this page.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">2. Vehicle Purchase Terms</h2>
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">2.1 Vehicle Information</h3>
            <p className="text-gray-600 mb-4">
              All vehicle information, including specifications, condition, and images, is provided to 
              the best of our knowledge. While we strive for accuracy, minor variations may exist. 
              Customers are encouraged to request additional information or inspection reports.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">2.2 Pricing</h3>
            <p className="text-gray-600 mb-4">
              All prices are quoted in USD and JPY. FOB (Free On Board) prices include the vehicle and 
              charges up to loading at the Japanese port. Freight, insurance, and destination charges 
              are additional and will be quoted separately.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">2.3 Payment Terms</h3>
            <ul className="list-disc pl-6 text-gray-600 mb-4">
              <li>Full payment is required before shipment</li>
              <li>Payment via international bank transfer (TT) or other agreed methods</li>
              <li>Payment must be received within 7 days of proforma invoice issuance</li>
              <li>Vehicle will not be shipped until payment is confirmed</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">3. Shipping & Delivery</h2>
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.1 Shipping Arrangements</h3>
            <p className="text-gray-600 mb-4">
              We arrange shipping to your destination port. Shipping times are estimates and may vary 
              based on vessel schedules, weather, and port conditions. We are not responsible for delays 
              beyond our control.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.2 Risk Transfer</h3>
            <p className="text-gray-600 mb-4">
              Risk transfers to the buyer upon loading of the vehicle onto the vessel at the Japanese port. 
              We recommend marine insurance for transit protection.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.3 Customs & Import Duties</h3>
            <p className="text-gray-600 mb-4">
              Buyers are responsible for all customs clearance, import duties, taxes, and fees in their 
              destination country. We provide necessary export documents but cannot advise on specific 
              import regulations.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">4. Inspection & Condition</h2>
            <p className="text-gray-600 mb-4">
              All vehicles are inspected before export. Inspection reports are available upon request. 
              Vehicles are sold in "as-is" condition. We do not provide warranties unless explicitly stated.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">5. Cancellation & Refunds</h2>
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">5.1 Buyer Cancellation</h3>
            <p className="text-gray-600 mb-4">
              Cancellations must be made in writing. If cancellation occurs after payment but before 
              shipping, a cancellation fee of 10% of the vehicle price applies to cover administrative 
              costs and potential market losses.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">5.2 No Refunds After Shipping</h3>
            <p className="text-gray-600 mb-4">
              Once a vehicle has been shipped, no refunds or cancellations are possible. Buyers should 
              ensure all details are confirmed before authorizing shipment.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">6. Limitation of Liability</h2>
            <p className="text-gray-600 mb-4">
              Global Trade Car's liability is limited to the FOB purchase price of the vehicle. We are 
              not liable for consequential damages, lost profits, or other indirect losses. Maximum 
              liability in any circumstance shall not exceed the amount paid for the vehicle.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">7. Intellectual Property</h2>
            <p className="text-gray-600 mb-4">
              All content on this website, including text, images, logos, and software, is the property 
              of Global Trade Car or its licensors. Reproduction or redistribution without written 
              permission is prohibited.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">8. Dispute Resolution</h2>
            <p className="text-gray-600 mb-4">
              Any disputes arising from these terms shall be resolved through good-faith negotiation. 
              If resolution cannot be reached, disputes shall be subject to the jurisdiction of Japanese courts.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">9. Changes to Terms</h2>
            <p className="text-gray-600 mb-4">
              We reserve the right to modify these terms at any time. Changes will be posted on this page 
              with an updated revision date. Continued use of our services constitutes acceptance of modified terms.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">10. Contact Information</h2>
            <p className="text-gray-600 mb-4">
              For questions about these terms and conditions, please contact us:
            </p>
            <ul className="list-none text-gray-600 mb-4">
              <li>Email: sales@globaltradecar.com</li>
              <li>Phone: +81 70-9270-2242</li>
            </ul>
          </CardContent>
        </Card>
      </div>

      <PublicFooter />
      <ScrollToTop />
    </div>
  );
};

export default TermsPage;